import { useState } from "react";
import { useNavigate } from "react-router-dom";

import MachinesTableHeader from "./MachinesTableHeader";
import {
  useDeleteMachineMutation,
  useGetMachinesQuery,
  useToggleActiveMutation,
  useToggleBlockMutation,
} from "@/features/models/machinesApi";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { createColumns } from "./columns";
import DataTable from "@/components/shared/DataTable";
import { useSelector } from "react-redux";
import { selectUser } from "@/features/auth/authSlice";

export default function MachinesPage() {
  const currentUser = useSelector(selectUser);
  const navigate = useNavigate();
  const [deleteMachine, { isLoading: isDeleting }] = useDeleteMachineMutation();
  const [toggleBlock] = useToggleBlockMutation();
  const [toggleActive] = useToggleActiveMutation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [machineToDelete, setMachineToDelete] = useState(null);

  // Filter states
  const [searchValue, setSearchValue] = useState("");
  const [sizeValue, setSizeValue] = useState("");
  const [wilayaValue, setWilayaValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortValue, setSortValue] = useState("created_at");
  const [statusValue, setStatusValue] = useState("all");

  const config = {
    title: "All Machines",
    description: "Manage all registered machines",
    badgeVariant: "outline",
  };

  const handleDeleteClick = (machine) => {
    setMachineToDelete(machine);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!machineToDelete) return;

    try {
      await deleteMachine(machineToDelete.id).unwrap();
      toast.success("Machine deleted successfully");
      setDeleteDialogOpen(false);
      setMachineToDelete(null);
    } catch (error) {
      toast.error("Failed to delete machine");
      console.error("Delete error:", error);
    }
  };

  const handleEditClick = (machine) => {
    navigate(`/dashboard/machines/edit/${machine.id}`);
  };

  const handleToogleBlock = async (machine) => {
    if (!machine) return;

    try {
      await toggleBlock(machine.id).unwrap();
      toast.success(
        `Machine ${machine.blocked ? "unblocked" : "blocked"} successfully`
      );
    } catch (error) {
      toast.error("Failed to toggle block status");
      console.error("Toggle block error:", error);
    }
  };

  const handleToogleActive = async (machine) => {
    if (!machine) return;

    try {
      await toggleActive(machine.id).unwrap();
      toast.success(
        `Machine ${machine.isActive ? "deactivated" : "activated"} successfully`
      );
    } catch (error) {
      toast.error("Failed to toggle active status");
      console.error("Toggle active error:", error);
    }
  };

  // Reset page when filters change
  const handleSearchChange = (value) => {
    setSearchValue(value);
    setCurrentPage(1);
  };
  const handleSizeChange = (val) => {
    setSizeValue(val === "all" ? "" : val);
    setCurrentPage(1);
  };
  const handleWilayaChange = (val) => {
    setWilayaValue(val === "all" ? "" : val);
    setCurrentPage(1);
  };
  const handleSortChnage = (val) => {
    setSortValue(val);
    setCurrentPage(1);
  };
  const handleStatusChange = (val) => {
    setStatusValue(val);
    setCurrentPage(1);
  };

  // Create columns with callback functions
  const columns = createColumns(
    handleDeleteClick,
    handleEditClick,
    handleToogleBlock,
    handleToogleActive
  );

  return (
    <div className="p-6">
      <div className="justify-between flex items-center mb-4">
        <div>
          <h1 className="text-2xl font-semibold">{config.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {config.description}
          </p>
        </div>
        {currentUser.role === "admin" && (
          <Button
            variant="outline"
            className="h-8"
            onClick={() => {
              navigate("/dashboard/machines/new");
            }}
          >
            Add Machine
          </Button>
        )}
      </div>

      {/* Filters moved to header */}
      <DataTable
        columns={columns}
        queryHook={useGetMachinesQuery}
        queryParams={{
          size: sizeValue,
          wilaya: wilayaValue,
          sort: sortValue,
          isActive: statusValue !== "all" ? statusValue.toString() : null,
        }}
        searchValue={searchValue}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        header={
          <MachinesTableHeader
            searchValue={searchValue}
            onSearchChange={handleSearchChange}
            sizeValue={sizeValue}
            wilayaValue={wilayaValue}
            onSizeChange={handleSizeChange}
            onWilayaChange={handleWilayaChange}
            sortValue={sortValue}
            onSortChange={handleSortChnage}
            statusValue={statusValue}
            onStatusChange={handleStatusChange}
          />
        }
        onSelectionChange={(selectedRows) => {
          // console.log("Selected machines:", selectedRows);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Machine</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the machine "{machineToDelete?.id}
              "? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
